import {
  ONBOARDING_CHECK_USER_REQUEST,
  ONBOARDING_CHECK_USER_SUCCESS,
  ONBOARDING_CHECK_USER_FAILURE,
  ONBOARDING_CHECK_ORGANIZATION_SUCCESS,
  ONBOARDING_ASSIGN_ORGANIZATION_SUCCESS,
  ONBOARDING_CREATE_ORGANIZATION_SUCCESS,
  ONBOARDING_UPDATE_USER_INFO_SUCCESS,
  ONBOARDING_ASSIGN_ROLE_SUCCESS,
  ONBOARDING_SET_PREFERENCES_SUCCESS,
  ONBOARDING_GET_USER_INFO_SUCCESS,
  ONBOARDING_REQUEST_JOIN_SUCCESS,
  ONBOARDING_CLEAR,
  ONBOARDING_SET_STEP,
  OnboardingState,
} from '../types/onboarding';

interface Action {
  type: string;
  payload?: any;
}

const initialState: OnboardingState = {
  currentStep: null,
  user: null,
  selectedOrganization: null,
  matchingOrganizations: [],
  newOrganization: null,
  joinRequest: null,
  userRole: null,
  userFirstName: null,
  userLastName: null,
  userEmail: null,
  preferences: {},
  userPermissions: null,
  isLoading: false,
  error: null,
  isComplete: false,
  onboardingStatus: 'pending',
};

const onboardingReducer = (state: OnboardingState = initialState, action: Action): OnboardingState => {
  switch (action.type) {
    case ONBOARDING_CHECK_USER_REQUEST:
    case 'ONBOARDING_CHECK_ORGANIZATION_REQUEST':
    case 'ONBOARDING_ASSIGN_ORGANIZATION_REQUEST':
    case 'ONBOARDING_CREATE_ORGANIZATION_REQUEST':
    case 'ONBOARDING_UPDATE_USER_INFO_REQUEST':
    case 'ONBOARDING_ASSIGN_ROLE_REQUEST':
    case 'ONBOARDING_SET_PREFERENCES_REQUEST':
    case 'ONBOARDING_GET_USER_INFO_REQUEST':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case ONBOARDING_CHECK_USER_SUCCESS:
      // Always show organization confirmation, then user profile setup
      return {
        ...state,
        isLoading: false,
        user: action.payload.user,
        currentStep: 'organization',
      };

    case ONBOARDING_CHECK_ORGANIZATION_SUCCESS:
      // API returns matching_organizations as an array
      const matchingOrgs = action.payload.domain_match && action.payload.matching_organizations?.length > 0
        ? action.payload.matching_organizations
        : [];

      // If domain matches, show the matching org options, but don't set selectedOrganization yet.
      // The user must explicitly join (which calls assignOrganization) before we assign them.
      // This prevents race conditions between local state and backend assignment.
      return {
        ...state,
        isLoading: false,
        matchingOrganizations: matchingOrgs,
        currentStep: 'organization',
      };

    case ONBOARDING_ASSIGN_ORGANIZATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        selectedOrganization: action.payload.organization,
        user: state.user ? {
          ...state.user,
          organization_id: action.payload.organization.id
        } : null,
        currentStep: 'user',
      };

    case ONBOARDING_CREATE_ORGANIZATION_SUCCESS:
      const createdOrg = action.payload.organization || {
        id: action.payload.user?.organization_id,
        uuid: action.payload.organization_uuid,
        name: 'New Organization',
        type: 'law_firm',
        email_domain: '',
      };

      return {
        ...state,
        isLoading: false,
        newOrganization: createdOrg,
        selectedOrganization: createdOrg,
        user: state.user ? {
          ...state.user,
          organization_id: createdOrg.id
        } : null,
        currentStep: 'user',
      };

    case ONBOARDING_UPDATE_USER_INFO_SUCCESS:
      return {
        ...state,
        isLoading: false,
        userFirstName: action.payload.first_name || null,
        userLastName: action.payload.last_name || null,
        userEmail: action.payload.email || null,
        userRole: action.payload.role || null,
        user: state.user ? {
          ...state.user,
          first_name: action.payload.first_name,
          last_name: action.payload.last_name,
          email: action.payload.email,
          role: action.payload.role,
        } : null,
        currentStep: 'preferences',
      };

    case ONBOARDING_ASSIGN_ROLE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        userRole: action.payload.role,
        user: state.user ? { ...state.user, role: action.payload.role } : null,
        currentStep: 'preferences',
      };

    case ONBOARDING_SET_PREFERENCES_SUCCESS:
      return {
        ...state,
        isLoading: false,
        preferences: action.payload.preferences,
        user: state.user ? { ...state.user, preferences: action.payload.preferences } : null,
        isComplete: true,
        currentStep: null,
      };

    case ONBOARDING_REQUEST_JOIN_SUCCESS:
      return {
        ...state,
        isLoading: false,
        joinRequest: action.payload.join_request,
        onboardingStatus: 'pending',  // Waiting for admin approval
        currentStep: 'organization',  // Stay on org selection until approved
      };

    case ONBOARDING_GET_USER_INFO_SUCCESS:
      const user = action.payload.user;
      // Always start with welcome for new users, then organization confirmation, then profile, then preferences
      const initialStep = 'welcome';
      // If user has organization, pre-populate it in selectedOrganization for display
      // Use org_name from payload if available, otherwise use a default
      const selectedOrg = user?.organization_id ? {
        id: String(user.organization_id),
        uuid: user.organization_uuid_encrypted || '',
        name: action.payload.org_name || user.organization_id?.toString() || 'Your Organization',
        type: 'law_firm',
        email_domain: '',
      } : null;
      return {
        ...state,
        isLoading: false,
        user: user,
        userPermissions: action.payload.permissions,
        currentStep: initialStep,
        selectedOrganization: selectedOrg,
      };

    case ONBOARDING_CHECK_USER_FAILURE:
    case 'ONBOARDING_CHECK_ORGANIZATION_FAILURE':
    case 'ONBOARDING_ASSIGN_ORGANIZATION_FAILURE':
    case 'ONBOARDING_CREATE_ORGANIZATION_FAILURE':
    case 'ONBOARDING_UPDATE_USER_INFO_FAILURE':
    case 'ONBOARDING_ASSIGN_ROLE_FAILURE':
    case 'ONBOARDING_SET_PREFERENCES_FAILURE':
    case 'ONBOARDING_GET_USER_INFO_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case ONBOARDING_SET_STEP:
      return {
        ...state,
        currentStep: action.payload,
      };

    case ONBOARDING_CLEAR:
      return initialState;

    default:
      return state;
  }
};

export default onboardingReducer;
